import type { FC, ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { useInterval } from '@dhl/use-interval'
import { useIsOffline, NetworkStatus } from '@dhl/use-is-offline'

import { VersionContext } from './VersionContext.js'
import { fetchVersion } from './fetchVersion.js'

type Props = {
  children: ReactNode
  /**
   * url pointing to your version.json
   */
  url: string

  /**
   * Frontend version, which should be set in your index template
   */
  version?: string

  /** Set the delay for retrieving the version
   * @default
   * 600000 (10 minutes)
   */
  delay?: number

  /** Check if the CF worker config has changed and needs to refresh the application
   * @default
   * false
   */
  checkConfig?: boolean
}

/** our default fetch delay for checking for a new version, our default is 10 minutes */
export const defaultFetchDelay: number = 10 * 60_000

let networkStatus: NetworkStatus = 'online'

export const VersionProvider: FC<Props> = ({
  children,
  url,
  version = 'latest',
  delay = defaultFetchDelay,
  checkConfig = false,
}) => {
  const [isReloadable, setIsReloadable] = useState(false)
  const [shouldUpdate, setShouldUpdate] = useState(false)

  const prevNetworkStatus = networkStatus
  networkStatus = useIsOffline() ? 'offline' : 'online'

  const handleUpdate = async () => {
    const doUpdate = await fetchVersion(url, version, checkConfig)

    if (doUpdate) {
      setShouldUpdate(true)
    } else {
      setShouldUpdate(false)
    }
  }

  useEffect(() => {
    if (isReloadable && shouldUpdate) {
      window.location.reload()
    }
  }, [shouldUpdate, isReloadable])

  useInterval(() => handleUpdate(), networkStatus === 'online' && version !== 'latest' ? delay : null)

  if (prevNetworkStatus === 'offline' && networkStatus === 'online' && version !== 'latest') {
    handleUpdate()
  }

  const value = useMemo(
    () => ({
      isReloadable,
      setIsReloadable,
      version,
      shouldUpdate,
    }),
    // reason: handlers itself not part of chain
    [isReloadable, setIsReloadable, version, shouldUpdate]
  )

  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>
}
